import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  IdentificationCard, CreditCard, Files, UploadSimple, CheckCircle, Circle,
  DownloadSimple, SignOut, Clock, ShieldCheck, Receipt, ChartLineUp, Buildings, PencilSimple,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageThread } from "@/components/MessageThread";

const SLOT_META = {
  id_front: { Icon: IdentificationCard, hint: "Front of your ID / driver's license / passport." },
  id_back: { Icon: IdentificationCard, hint: "Back of your ID / driver's license." },
  ssn_card: { Icon: CreditCard, hint: "Front of your Social Security card." },
  other: { Icon: Files, hint: "Any additional document we've asked you to provide." },
};
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function UploadSlot({ slotKey, label, onUploaded, done }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const meta = SLOT_META[slotKey] || SLOT_META.other;
  const { Icon } = meta;
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("slot", slotKey);
      await api.post("/portal/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`); onUploaded();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-5 flex flex-col" data-testid={`upload-slot-${slotKey}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0"><Icon size={22} /></div>
        <div className="font-medium text-sm flex items-center gap-2">{label}{done && <CheckCircle size={16} weight="fill" className="text-emerald-500" />}</div>
      </div>
      <p className="text-xs text-muted-foreground mb-4 flex-1">{meta.hint}</p>
      <input ref={inputRef} type="file" className="hidden" onChange={onChange} accept="image/*,application/pdf" data-testid={`file-input-${slotKey}`} />
      <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`upload-button-${slotKey}`} className="w-full gap-2 bg-primary text-primary-foreground hover:bg-[#DBC197]">
        {busy ? "Uploading…" : <><UploadSimple size={18} weight="bold" /> {done ? "Replace" : "Upload"}</>}
      </Button>
    </div>
  );
}

const Step = ({ done, n, title }) => (
  <div className="flex items-center gap-3">
    {done ? <CheckCircle size={22} weight="fill" className="text-emerald-500 shrink-0" /> : <Circle size={22} className="text-zinc-600 shrink-0" />}
    <span className={`text-sm ${done ? "text-foreground" : "text-muted-foreground"}`}><span className="text-primary font-medium mr-1.5">{n}.</span>{title}</span>
  </div>
);

const NoAccess = ({ reason, name, onLogout }) => {
  const m = {
    pending: { t: "Your account is pending approval", b: "Our team is reviewing your information. You'll be able to upload documents here once you're approved." },
    no_access: { t: "Portal access not enabled yet", b: "Your profile exists, but portal access hasn't been turned on. Please contact us." },
    no_profile: { t: "We couldn't find your client profile", b: "Please contact DAFARGOS HOLDINGS so we can link your account." },
    admin: { t: "You're signed in as an administrator", b: "Head to your Command Center to manage clients." },
  }[reason] || { t: "No access", b: "" };
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="portal-no-access">
      <div className="max-w-md w-full text-center">
        <div className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-6"><Clock size={28} /></div>
        <h1 className="font-serif text-3xl mb-3">{m.t}</h1>
        {name && <p className="text-primary mb-2">Hello, {name}</p>}
        <p className="text-muted-foreground mb-8">{m.b}</p>
        <Button variant="outline" onClick={onLogout} className="gap-2 border-white/15" data-testid="portal-logout-noaccess"><SignOut size={18} /> Sign out</Button>
      </div>
    </div>
  );
};

export default function Portal() {
  const { user, logout } = useAuth();
  const [state, setState] = useState(null);
  const [signKey, setSignKey] = useState(null);
  const [sigName, setSigName] = useState("");
  const [sigConsent, setSigConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [bizOpen, setBizOpen] = useState(false);
  const [bizForm, setBizForm] = useState({});
  const [bizSaving, setBizSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/portal/me"); setState(data); }
    catch { setState({ access: false, reason: "no_profile" }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async (doc) => {
    try {
      const res = await api.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };
  const submitSign = async () => {
    if (!sigName.trim()) return toast.error("Type your full legal name");
    if (!sigConsent) return toast.error("Please check the consent box");
    setSigning(true);
    try {
      await api.post(`/portal/agreements/${signKey}/sign`, { signature: sigName, full_name: sigName });
      toast.success("Document signed"); setSignKey(null); setSigName(""); setSigConsent(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to sign"); }
    finally { setSigning(false); }
  };
  const openBiz = () => { const c = state.company || {}; setBizForm({ legal_name: c.legal_name || "", dba: c.dba || "", address: c.address || "", phone: c.phone || "", email: c.email || "", website: c.website || "", industry: c.industry || "" }); setBizOpen(true); };
  const submitBiz = async () => {
    setBizSaving(true);
    try {
      await api.post("/portal/company/request-changes", { changes: bizForm });
      toast.success("Changes submitted for approval"); setBizOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBizSaving(false); }
  };

  if (state === null) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  if (!state.access) return <NoAccess reason={state.reason} name={state.name} onLogout={logout} />;

  const { profile, documents = [], invoices = [], scores = [], agreements = [], company } = state;
  const has = (slot) => documents.some((d) => (d.name || "").startsWith(state.slots?.[slot] || "###"));
  const idDone = has("id_front") && has("id_back");
  const ssnDone = has("ssn_card");
  const signedCount = agreements.filter((a) => a.signed).length;
  const totalDue = invoices.filter((i) => i.status !== "Paid").reduce((s, i) => s + Number(i.balance || 0), 0);
  const latest = scores.length ? scores[scores.length - 1] : null;

  return (
    <div className="min-h-screen bg-background" data-testid="portal-page">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="DAFARGOS HOLDINGS LLC" className="h-10 w-10 object-contain rounded-lg" />
            <div>
              <div className="font-display text-lg tracking-tighter text-primary">DAFARGOS HOLDINGS LLC</div>
              <div className="text-xs text-muted-foreground">Client Portal</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-2 text-muted-foreground" data-testid="portal-logout"><SignOut size={18} /> Sign out</Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-10">
        <section className="rounded-2xl border border-white/10 bg-zinc-900/50 p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="overline text-primary mb-1">Welcome</p>
              <h1 className="font-serif text-3xl">{profile.full_name || profile.business_name || user?.email}</h1>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs text-primary" data-testid="portal-status"><ShieldCheck size={14} weight="fill" /> {profile.status || "Active"}</span>
          </div>
          {profile.service_package && <div className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm" data-testid="portal-service"><span className="text-muted-foreground">Service:</span><span className="font-medium">{profile.service_package}</span></div>}
        </section>

        {/* Onboarding checklist */}
        <section className="rounded-2xl border border-primary/20 bg-primary/5 p-6" data-testid="portal-onboarding">
          <h2 className="font-serif text-2xl mb-1">Complete your client profile</h2>
          <p className="text-muted-foreground text-sm mb-5">Follow these steps to get set up. Everything you submit is stored securely and attaches to your profile.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Step n="1" done title="Sign up & access your portal" />
            <Step n="2" done={idDone} title="Upload your ID / driver's license or passport (front & back)" />
            <Step n="3" done={ssnDone} title="Upload your Social Security card" />
            <Step n="4" done={documents.length > 0} title="Upload any other requested documents" />
            <Step n="5" done={signedCount >= agreements.length && agreements.length > 0} title="Sign your 2 authorization documents (right here — no email needed)" />
          </div>
        </section>

        {/* Uploads */}
        <section>
          <h2 className="font-serif text-2xl mb-1">Upload your documents</h2>
          <p className="text-muted-foreground mb-6 text-sm">Securely submit the documents below.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <UploadSlot slotKey="id_front" label="ID — Front" onUploaded={load} done={has("id_front")} />
            <UploadSlot slotKey="id_back" label="ID — Back" onUploaded={load} done={has("id_back")} />
            <UploadSlot slotKey="ssn_card" label="Social Security Card" onUploaded={load} done={has("ssn_card")} />
            <UploadSlot slotKey="other" label="Other Document" onUploaded={load} done={false} />
          </div>
        </section>

        {/* Agreements to sign */}
        <section data-testid="portal-agreements">
          <h2 className="font-serif text-2xl mb-1">Documents to sign</h2>
          <p className="text-muted-foreground mb-6 text-sm">Review and sign your authorization documents electronically. A signed copy is saved to your profile automatically.</p>
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5">
            {agreements.map((a) => (
              <div key={a.key} className="flex items-center justify-between gap-4 px-4 py-4" data-testid={`agreement-${a.key}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {a.signed ? <CheckCircle size={20} weight="fill" className="text-emerald-500 shrink-0" /> : <Circle size={20} className="text-zinc-600 shrink-0" />}
                  <span className="text-sm font-medium truncate">{a.title}</span>
                </div>
                {a.signed ? <span className="text-xs text-emerald-400">Signed</span> :
                  <Button size="sm" onClick={() => { setSignKey(a.key); setSigName(profile.full_name || ""); setSigConsent(false); }} data-testid={`sign-${a.key}`} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">Sign now</Button>}
              </div>
            ))}
          </div>
        </section>

        {/* Invoices */}
        {invoices.length > 0 && (
          <section data-testid="portal-invoices">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl flex items-center gap-2"><Receipt size={22} className="text-primary" /> Invoices</h2>
              {totalDue > 0 && <span className="text-sm">Total due: <span className="font-display text-xl text-primary">{money(totalDue)}</span></span>}
            </div>
            <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5">
              {invoices.map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-4 px-4 py-3.5" data-testid={`invoice-${i.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{i.description}</div>
                    <div className="text-xs text-muted-foreground">{i.status === "Paid" ? "Paid in full" : `Balance ${money(i.balance)}`}{i.method ? ` · pay by ${i.method}` : ""}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-lg tracking-tighter">{money(i.amount)}</div>
                    <div className={`text-xs ${i.status === "Paid" ? "text-emerald-400" : "text-amber-400"}`}>{i.status}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Credit score */}
        {scores.length > 0 && (
          <section data-testid="portal-scores">
            <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><ChartLineUp size={22} className="text-primary" /> Your credit score</h2>
            <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-6 flex items-end gap-6">
              <div>
                <div className="font-display text-5xl tracking-tighter text-primary">{latest.score}</div>
                <div className="text-xs text-muted-foreground">{latest.bureau} · {latest.date}</div>
              </div>
              <div className="flex items-end gap-1 h-16 flex-1">
                {scores.slice(-12).map((s) => <div key={s.id} className="flex-1 bg-primary/30 rounded-t" style={{ height: `${Math.max(8, Math.min(100, ((s.score - 300) / 550) * 100))}%` }} title={`${s.score} (${s.date})`} />)}
              </div>
            </div>
          </section>
        )}

        {/* Business info */}
        {company && (
          <section data-testid="portal-business">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl flex items-center gap-2"><Buildings size={22} className="text-primary" /> Your business</h2>
              <Button size="sm" variant="outline" className="gap-2 border-white/15" onClick={openBiz} data-testid="request-biz-changes"><PencilSimple size={15} /> Request changes</Button>
            </div>
            {state.pending_change && <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-200" data-testid="biz-pending">You have requested changes awaiting admin approval.</div>}
            <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-6 grid sm:grid-cols-2 gap-4 text-sm">
              {[["Legal name", company.legal_name], ["DBA", company.dba], ["Address", company.address], ["Phone", company.phone], ["Email", company.email], ["Website", company.website], ["Industry", company.industry]].map(([k, v]) => (
                <div key={k}><div className="text-xs uppercase tracking-wider text-muted-foreground">{k}</div><div className="mt-0.5">{v || "—"}</div></div>
              ))}
            </div>
          </section>
        )}

        {/* Messages */}
        <section data-testid="portal-messages">
          <h2 className="font-serif text-2xl mb-4">Messages</h2>
          <MessageThread listUrl="/portal/messages" postUrl="/portal/messages" self="client" />
        </section>

        {/* Submitted documents */}
        <section>
          <h2 className="font-serif text-2xl mb-4">Submitted documents</h2>
          {documents.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 p-10 text-center text-muted-foreground" data-testid="portal-no-docs">No documents submitted yet.</div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="portal-documents">
              {documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-4 px-4 py-4" data-testid={`portal-doc-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle size={20} weight="fill" className="text-emerald-500 shrink-0" />
                    <div className="min-w-0"><div className="text-sm font-medium truncate">{d.category}</div><div className="text-xs text-muted-foreground truncate">{d.original_filename}</div></div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => download(d)} data-testid={`portal-download-${d.id}`}><DownloadSimple size={18} /></Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Sign dialog */}
      <Dialog open={!!signKey} onOpenChange={(o) => { if (!o) setSignKey(null); }}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-md">
          <DialogHeader><DialogTitle>Electronic signature</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Type your full legal name to sign. This is legally binding under the E-SIGN Act.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Full legal name</Label><Input data-testid="signature-input" value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder="Your full name" className="mt-1" /></div>
            {sigName && <div className="rounded-lg border border-white/10 p-4 text-center"><span className="text-2xl" style={{ fontFamily: "cursive" }}>{sigName}</span></div>}
            <label className="flex items-start gap-2 text-xs text-muted-foreground"><Checkbox checked={sigConsent} onCheckedChange={(v) => setSigConsent(!!v)} data-testid="signature-consent" /> I agree that typing my name serves as my electronic signature and I consent to signing this document electronically.</label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSignKey(null)}>Cancel</Button>
            <Button disabled={signing} onClick={submitSign} data-testid="submit-signature" className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{signing ? "Signing…" : "Sign document"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business change request dialog */}
      <Dialog open={bizOpen} onOpenChange={setBizOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>Request business info changes</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Your changes are sent to DAFARGOS for approval before they go live.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {[["legal_name", "Legal name"], ["dba", "DBA"], ["address", "Address"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["industry", "Industry"]].map(([k, label]) => (
              <div key={k}><Label>{label}</Label><Input data-testid={`biz-${k}-input`} value={bizForm[k] || ""} onChange={(e) => setBizForm((f) => ({ ...f, [k]: e.target.value }))} /></div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBizOpen(false)}>Cancel</Button>
            <Button disabled={bizSaving} onClick={submitBiz} data-testid="submit-biz-changes" className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{bizSaving ? "Submitting…" : "Submit for approval"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
